import React, { useState, useEffect, useRef, useContext } from 'react';
import LayoutSelectButton from 'components/layout/LayoutSelectButton.jsx';
import LayoutSvg from 'components/layout/LayoutSvg.jsx';
import LayoutMostEfficient from 'components/layout/LayoutMostEfficient.jsx';
import LayoutRedrawForm from 'components/layout/LayoutRedrawForm.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import RedrawDataToPost from  'components/layout/RedrawDataToPost.jsx';

const LayoutNavSubSection = ( {sub_section, index, activeRedraws, setActiveRedraws, activeTab, items, setItems, requiredPartsMissingError, selectOnMount, memberToSelect, ranksTemplates }) => {
  const myLayoutContext = useContext(LayoutContext);
  const [selectedTemplateCount, setSelectedTemplateCount] = useState(0);
  // Seed from the server-computed list so the badge appears immediately on
  // page load and the default selection below can match against it. The
  // post-redraw fetchMostEfficients call replaces this state once the user's
  // changes are reflected. Server payload shape matches the AJAX response:
  // an array of { uuid } objects.
  const [mostEfficients, setMostEfficients] = useState(sub_section.most_efficient_member_uuids || []);
  const [redrawRequested, setRedrawRequested] = useState(false);
  const [redrawInProgress, setRedrawInProgress] = useState(false);
  const recoveryApplyMethods = useRef({});
  const formRef = useRef();
  const [item, _setItem] = useState(null);
  const [activationResults, setActivationResults] = useState({});
  // Kept from the mount: a tab added later marks itself selected, which must not turn the first tab into one that never was.
  const selectedOnMount = useRef(selectOnMount);
  // A tab selected on load with a nest beside its templates draws nothing until the most-efficient answer says which
  // member to select, so the page never switches away from a member it drew. A section ranked with its nests leaves
  // that to its ranking (LayoutNavSection#selectRankedCombination).
  const [awaitingMostEfficient, setAwaitingMostEfficient] = useState(() => Boolean(selectOnMount) && !ranksTemplates &&
    sub_section.members.some((member) => member.dynamic_nesting) && sub_section.members.some((member) => !member.dynamic_nesting));
  const mostEfficientAsked = useRef(false);
  // The answer arrives after renders that may have changed what it was asked about.
  const selectedUuid = useRef(null);
  const formChanged = useRef(false);
  // The member the on-load answer selected, whose first drawing needs no second asking on the same form.
  const answeredOnLoad = useRef(null);

  const setItem = (member) => {
    if(item != null) { removeItem(item) }
    _setItem(member)
    selectedUuid.current = member.uuid;
    setItems((prev) => prev.concat([member]))
  }

  const removeItem = (member) => {
    _setItem(null)
    selectedUuid.current = null;
    setItems((prev) => prev.filter((item) => item.uuid != member.uuid))
  }

  const selectedItemCount = (sub_section) => {
    let item_uuids = items.map((i) => i.uuid );
    let sub_section_uuids = sub_section.members.map((m) => m.uuid);
    let intersection = item_uuids.filter(value => sub_section_uuids.includes(value));
    return intersection.length;
  }

  const isMostEfficient = (member_uuid) => {
    for(let key in mostEfficients) {
      let member = mostEfficients[key];
      if(member.uuid == member_uuid) {
        return true;
      }
    }
    return false;
  }

  // A tab of nests alone has no template to compare them with: they keep the flag they were given.
  const hasTemplates = () => sub_section.members.some((member) => !member.dynamic_nesting);

  const fetchMostEfficients = () => {
    if(item == null) { return }
    if(!hasTemplates()) { return }
    if(awaitingMostEfficient) { return }
    if(answeredOnLoad.current === item.uuid) {
      answeredOnLoad.current = null;
      return;
    }

    LayoutMostEfficient(sub_section.members, item.uuid, (result) => {
      setMostEfficients((prev) => [...result])
    })
  }

  const partNames = () => {
    return sub_section.members.map((member) => { return member.part_names}).flat().filter((v, i, a) => a.indexOf(v) === i)
  }

  const partNamesInfo = () => {
    return <>
      { sub_section.members.length > 0 && partNames(sub_section).length > 0 && (<div className="partNamesInfo">
        {myLayoutContext.locale =='tr' && (<>
          <strong> {partNames(sub_section).map(str => `"${str}"`).join(', ') }</strong> için yerleştirme şablonları <br />
            </>
          ) }

        {myLayoutContext.locale =='en' && (<>
          Nesting templates for <strong> {partNames(sub_section).map(str => `"${str}"`).join(', ') }. </strong><br />
            </>
          ) }

       </div> )}
      </>
  }

  useEffect(() => {
    if(activeRedraws.length == 0) {
      fetchMostEfficients();
    }
  }, [activeRedraws]);

  useEffect(() => {
    if(selectedOnMount.current && item == null && sub_section.members.length > 0) {
      const namedMember = sub_section.members.find((m) => m.uuid === sub_section.selected_member_uuid);
      const mostEfficientUuids = (sub_section.most_efficient_member_uuids || []).map((entry) => entry.uuid);
      const mostEfficientMember = sub_section.members.find((m) => mostEfficientUuids.includes(m.uuid));
      setItem(namedMember || mostEfficientMember || sub_section.members[0]);
    }
  }, []);

  // The member selected on load's own form, which the member taking its place keeps (ChooseLayoutDimensions is not
  // remounted), is asked about once its drawing is requested: by then it holds the sheet the viewer last used. A
  // selection or this form changed meanwhile keeps what the viewer has, which then draws what the form now says.
  const selectMostEfficientOnLoad = (askedAbout) => (result) => {
    setMostEfficients(() => [...result]);
    const named = (member) => result.some((entry) => entry.uuid === member.uuid);
    const mostEfficient = sub_section.members.find(named);
    if(selectedUuid.current === askedAbout.uuid && !formChanged.current) {
      if(mostEfficient != null && !named(askedAbout)) {
        setRedrawRequested(() => false);
        setItem(mostEfficient);
      }
      answeredOnLoad.current = selectedUuid.current;
    }
    setAwaitingMostEfficient(false);
  };

  useEffect(() => {
    if(!awaitingMostEfficient || !redrawRequested || item == null || mostEfficientAsked.current) { return; }

    mostEfficientAsked.current = true;
    formChanged.current = false;
    if(formRef.current) { formRef.current.className = 'disabledForm'; }
    LayoutMostEfficient(sub_section.members, item.uuid, selectMostEfficientOnLoad(item), () => setAwaitingMostEfficient(false));
  }, [redrawRequested]);

  // The nest of the parts the viewer chose, when this tab already holds them (LayoutNavSection#nestChosenParts).
  useEffect(() => {
    if (memberToSelect == null) { return; }
    if (items.some((selected) => selected.uuid === memberToSelect.uuid)) { return; }

    setItem(memberToSelect);
  }, [memberToSelect]);

  const checkActivationFormulaResult = ( member_uuids, activeMemberUUID ) => {
    const activeMember = sub_section.members.find((m) => m.uuid === activeMemberUUID);
    let dataToPost = RedrawDataToPost( { uuid: activeMemberUUID, member: activeMember } ) ;
    dataToPost['member_uuids'] = member_uuids;

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dataToPost)
    };

    fetch(`/layouts/${activeMemberUUID}/layout_template_activation`, requestOptions)
      .then(response => response.json())
      .then((data) => {
        for (const [uuid, res] of Object.entries(data.results)) {
          setActivationResults((prev) => {
            prev[uuid] = res;
            return { ...prev }
          })
        }
      })
      .catch(showUncompared);
  };

  // As a tab opened before any redraw of it shows its members: every template, a nest beside them hidden until one
  // compares it. Only where nothing has said otherwise.
  const showUncompared = () => {
    sub_section.members.forEach((member) => {
      setActivationResults((prev) => (prev[member.uuid] === undefined ? { ...prev, [member.uuid]: !(member.dynamic_nesting && hasTemplates()) } : prev));
    });
  };

  // A nest hidden for not beating the templates is no template hidden as a duplicate.
  const anyHiddenButtons = () => {
    for (const [uuid, res] of Object.entries(activationResults)) {
      if(res == false && !sub_section.members.some((member) => member.uuid == uuid && member.dynamic_nesting)) {
        return true;
      }
    }
    return false;
  };

  const areButtonsLoading = () => {
    // If we have members but no activation results yet, we're still loading
    if(sub_section.members.length > 0 && Object.keys(activationResults).length === 0) {
      return true;
    }
    // Check if we're waiting for some activation results
    const resultsCount = sub_section.members.filter(m => activationResults[m.uuid] === true).length;
    return resultsCount < sub_section.members.length && resultsCount === 0;
  };

  const memberUuids = () => {
    return sub_section.members.map(member => member.uuid);
  }

  const HandleActivationFormula = () => {
    let listener = function(e) {
      if(memberUuids().includes(e.detail)) {
        checkActivationFormulaResult(memberUuids(), e.detail);
      }
    };

    document.addEventListener('layout_redraw_finished', listener);
    return () => { document.removeEventListener('layout_redraw_finished', listener); }
  }

  useEffect(HandleActivationFormula, []);

  // A redraw that failed says nothing of which templates to show.
  useEffect(() => {
    let listener = function(e) {
      if(memberUuids().includes(e.detail)) { showUncompared(); }
    };

    document.addEventListener('layout_redraw_failed', listener);
    return () => { document.removeEventListener('layout_redraw_failed', listener); }
  }, []);

  // A nest beside templates waits for a redraw here to be compared with them. A tab selected on load waits for that
  // redraw to say which of its templates show (checkActivationFormulaResult).
  useEffect(() => {
    if(index == activeTab && !selectedOnMount.current) {
      sub_section.members.map((member) => {
        setActivationResults((prev) => {
          prev[member.uuid] = member.dynamic_nesting && hasTemplates() ? (prev[member.uuid] ?? false) : true;
          return { ...prev }
        })
      });
    }
  }, [activeTab])

  const getLayoutSelectButton = (member, index) => {
    return activationResults[member.uuid] && <LayoutSelectButton key={member.uuid}
      activeRedraws={activeRedraws}
      member={member}
      removeItem={removeItem}
      setItem={setItem}
      items={items}
      index={index}
      isMostEfficient={isMostEfficient(member.uuid)}
    />;
  }

  return (
    <div key={`all_body_cont_${index}`} className={`${index != activeTab && 'hidden'}`}>
      <div className="col-lg-3 float-left layout-col-cont" onChangeCapture={() => { formChanged.current = true; }}
        onInputCapture={() => { formChanged.current = true; }}>
          { item != null && <LayoutRedrawForm
              formRef={formRef}
              item={item}
              setRedrawRequested={setRedrawRequested}
              recoveryApplyMethods={recoveryApplyMethods}
              redrawRequested={redrawRequested}
              redrawInProgress={redrawInProgress}
            /> }

            { item == null && <LayoutRedrawForm disabled={true} /> }

        </div>

        <div className="col-lg-9 float-right no-margin-right layout-svg-column">

          { partNamesInfo() }

          {item == null && partNamesInfo() == "" && <div>  <p> {window.wording_select_template} </p> <br /></div>}

          {areButtonsLoading() && <center> <img src="https://d2atdwxjx7uc4i.cloudfront.net/images/bx_loader.gif" /> </center> }

          {sub_section.members.map(getLayoutSelectButton) }

          {anyHiddenButtons() && (<> <small> <br /> <sup>*</sup>{t('layouts.note_some_templates_are_hidden')}  </small> </>) }

          {item != null && (
            <LayoutSvg klass={item.klass}
              id={item.id}
              redrawForm={formRef}
              visible={index == activeTab}
              parts={item.parts}
              redrawUrl={item.redraw_url}
              uuid={item.uuid}
              member={item}
              partNames={item.part_names}
              activeRedraws={activeRedraws}
              redrawRequested={redrawRequested}
              setRedrawRequested={setRedrawRequested}
              setActiveRedraws={setActiveRedraws}
              redrawInProgress={redrawInProgress}
              setRedrawInProgress={setRedrawInProgress}
              recoveryApplyMethods={recoveryApplyMethods}
              redrawHeld={awaitingMostEfficient}
            />
          )}

          {item == null && <><br /><br /></> }

           {requiredPartsMissingError() != '' && (
              <div className="col-lg-12 float-right">
                <div className="alert alert-danger fade in">
                  <span className="">
                    <i className="fa fa-exclamation-circle"></i>
                      <strong> {requiredPartsMissingError()} </strong>
                    </span>
                  <br />
                </div>
              </div>
            )}

        </div>
      </div>
      )

}

export default LayoutNavSubSection;
